import axios from 'axios';
import {
  BookAppointmentPayload,
  BookAppointmentResponse,
  DaySlot,
  GetAvailableSlotsResponse,
  TimeSlot,
} from './FOVScheduling.types';
import { post as cdpPost } from 'lib/utils/axiosCDPFetcher';
import { getCookie, getOrCreateEnrollmentId } from 'lib/utils/enrollmentUtil';
import { FOV_SOURCE, CDP_EVENTS } from 'src/constants';
import * as cdpService from '../../services/CdpService';
import { logger } from '../../logger';

/** Prefix applied to every Splunk log emitted from this module. */
const LOG_PREFIX = 'FOVScheduling';

const objectToQueryString = (obj: Record<string, any>): string =>
  Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&');

export interface SiteSchedulingParams {
  siteId: string;
  protocolId: string;
}
export interface SiteSchedulingSettings {
  siteId: string;
  protocolId: string;
  isFovSchedulingEnabled: boolean;
  siteDirectedScheduling: boolean;
  [key: string]: any;
}
export interface HasAvailableSlotsResponse {
  hasSlots: boolean;
  [key: string]: any;
}
export interface GetAvailableSlotsParams extends SiteSchedulingParams {
  currentDateTime: string;
}
interface RawAvailableSlot {
  slotId: number;
  slot: string;
  slotAvailableDate: string;
  startTimeLocal: string;
  endTimeLocal: string;
  slotDateTimeUtc: string;
  capacity: number;
}
interface RawGetAvailableSlotsResponse {
  siteId: string;
  protocolId: string;
  timezone: string;
  convertedUserDateTime: string;
  windowStartDate: string;
  windowEndDate: string;
  leadDays: number;
  bookingHorizonDays: number;
  availableSlots: RawAvailableSlot[];
}
export interface ValidateSlotResponse {
  slotId: number;
  isValid: boolean;
  remainingCapacity: number;
}

/**
 * Example: ("2026-06-20", "09:00:00", "EST") → "Jun 20, 2026, 9:00 AM EST"
 */
export const buildAppointmentDateTimeDisplay = (
  appointmentDateLocal?: string | null,
  appointmentTime?: string | null,
  appointmentTimezone?: string | null
): string | null => {
  if (!appointmentDateLocal) return null;

  const dateTime = new Date(`${appointmentDateLocal}T${appointmentTime ?? '00:00:00'}`);
  if (isNaN(dateTime.getTime())) return null;

  const datePart = dateTime.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Only append the time portion when an explicit time was provided.
  if (!appointmentTime) {
    return datePart;
  }

  const timePart = dateTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  const display = `${datePart}, ${timePart}`;
  return appointmentTimezone ? `${display} ${appointmentTimezone}` : display;
};

/** Format the current local datetime as ISO-8601 with offset. */
export const getCurrentDateTimeISO = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const off = -now.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const offH = pad(Math.floor(Math.abs(off) / 60));
  const offM = pad(Math.abs(off) % 60);
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}` +
    `${sign}${offH}:${offM}`
  );
};

const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/**
 * Enumerate every inclusive "YYYY-MM-DD" date between `startDate` and `endDate`.
 * Returns [] when either bound is missing/invalid or the range is inverted.
 */
const enumerateDateRange = (startDate?: string, endDate?: string): string[] => {
  if (!startDate || !endDate) return [];
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return [];

  const dates: string[] = [];
  const cursor = new Date(start);
  let guard = 0;
  while (cursor <= end && guard < 366) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    guard += 1;
  }
  return dates;
};

/** Build a single DaySlot from a date and its (possibly empty) raw slots. */
const buildDaySlot = (date: string, daySlots: RawAvailableSlot[]): DaySlot => {
  const dateObj = new Date(`${date}T00:00`);
  return {
    date,
    dayLabel: DAY_LABELS[dateObj.getDay()],
    dayNumber: dateObj.getDate(),
    // Sum remaining per-time capacities so the day badge reflects total available capacity.
    slotCount: daySlots.reduce((total, s) => {
      const capacity = s.capacity ?? 1;
      return capacity > 0 ? total + capacity : total;
    }, 0),
    timeSlots: daySlots.map(
      (s): TimeSlot => ({
        id: String(s.slotId),
        startTime: s.startTimeLocal || s.slot,
        endTime: s.endTimeLocal || '',
        slotsLeft: s.capacity ?? 1,
      })
    ),
  };
};

const mapToDaySlots = (raw: RawGetAvailableSlotsResponse): DaySlot[] => {
  const slots = raw.availableSlots ?? [];
  if (slots.length === 0) return [];

  // Group slots by date, preserving API order
  const grouped: Record<string, RawAvailableSlot[]> = {};
  slots.forEach((s) => {
    const key = s.slotAvailableDate;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  });

  const firstAvailableDate = slots[0]?.slotAvailableDate;

  const windowDates = enumerateDateRange(firstAvailableDate, raw.windowEndDate);
  const dates =
    windowDates.length > 0
      ? Array.from(new Set([...windowDates, ...Object.keys(grouped)]))
      : Object.keys(grouped);

  return dates
    .sort((a, b) => a.localeCompare(b))
    .map((date) => buildDaySlot(date, grouped[date] ?? []));
};

// ─── API calls ────────────────────────────────────────────────────────────────

export const hasAvailableSlots = async (
  params: SiteSchedulingParams & { currentDateTime: string }
): Promise<HasAvailableSlotsResponse> => {
  const { siteId, protocolId, currentDateTime } = params;
  const queryParams = { siteId, protocolId, currentDateTime };
  try {
    const response = await axios.get(
      `/api/dap-api?url=FOVScheduling/HasAvailableSlots&${objectToQueryString(queryParams)}`
    );
    const data = response?.data ?? { hasSlots: false };
    logger.info(`${LOG_PREFIX}: hasAvailableSlots success`, {
      siteId,
      protocolId,
      hasSlots: data?.hasSlots ?? false,
    });
    return data;
  } catch (error) {
    logger.error(`${LOG_PREFIX}: hasAvailableSlots failed`, { siteId, protocolId, error });
    throw error;
  }
};

export const getAvailableSlots = async (
  params: GetAvailableSlotsParams
): Promise<GetAvailableSlotsResponse> => {
  const { siteId, protocolId, currentDateTime } = params;
  const queryParams = { siteId, protocolId, currentDateTime };
  try {
    const response = await axios.get(
      `/api/dap-api?url=/FOVScheduling/GetAvailableSlots&${objectToQueryString(queryParams)}`
    );
    const raw: RawGetAvailableSlotsResponse = response?.data ?? {};
    const days = mapToDaySlots(raw);

    const result = {
      success: Array.isArray(raw.availableSlots),
      days,
      hasSlots: days.some((d) => d.slotCount > 0),
      timezone: raw.timezone ?? null,
    };
    logger.info(`${LOG_PREFIX}: getAvailableSlots success`, {
      siteId,
      protocolId,
      dayCount: days.length,
      hasSlots: result.hasSlots,
    });
    return result;
  } catch (error) {
    logger.error(`${LOG_PREFIX}: getAvailableSlots failed`, { siteId, protocolId, error });
    throw error;
  }
};

export const validateSlot = async (slotId: number): Promise<ValidateSlotResponse> => {
  const queryParams = { slotId };
  try {
    const response = await axios.get(
      `/api/dap-api?url=/FOVScheduling/ValidateSlot&${objectToQueryString(queryParams)}`
    );
    const data = response?.data ?? { slotId, isValid: false, remainingCapacity: 0 };
    logger.info(`${LOG_PREFIX}: validateSlot success`, {
      slotId,
      isValid: data?.isValid ?? false,
      remainingCapacity: data?.remainingCapacity ?? 0,
    });
    return data;
  } catch (error) {
    logger.error(`${LOG_PREFIX}: validateSlot failed`, { slotId, error });
    throw error;
  }
};

export const bookAppointment = async (
  payload: BookAppointmentPayload
): Promise<BookAppointmentResponse> => {
  const appointmentDateTimeDisplay =
    payload.appointmentDateTimeDisplay ??
    buildAppointmentDateTimeDisplay(
      payload.appointmentDateLocal,
      payload.appointmentTime,
      payload.appointmentTimezone
    );

  try {
    const response = await axios.post('/api/dap-api', {
      url: '/FOVScheduling/BookAppointment',
      data: {
        eprReferralId: payload.ePrId,
        externalParticipantId: payload.externalParticipantId ?? null,
        slotId: payload.slotId ?? null,
        appointmentDateLocal: payload.appointmentDateLocal ?? null,
        appointmentTime: payload.appointmentTime ?? null,
        appointmentTimezone: payload.appointmentTimezone ?? null,
        appointmentDateTimeDisplay,
        appointmentStatus: payload.appointmentStatus,
        bookingStatus: payload.bookingStatus ?? null,
        source: payload.source ?? FOV_SOURCE,
        integrationSource: payload.integrationSource ?? null,
        isHumanApiEnabled: payload.isHumanApiEnabled ?? false,
        protocolId: payload.protocolId ?? null,
      },
    });

    const data = response?.data ?? { id: 0, success: false, error: 'No response from server' };
    logger.info(`${LOG_PREFIX}: bookAppointment success`, {
      ePrId: payload.ePrId,
      appointmentStatus: payload.appointmentStatus,
      slotId: payload.slotId ?? null,
      success: data?.success ?? false,
    });
    return data;
  } catch (error) {
    logger.error(`${LOG_PREFIX}: bookAppointment failed`, {
      ePrId: payload.ePrId,
      appointmentStatus: payload.appointmentStatus,
      slotId: payload.slotId ?? null,
      error,
    });
    throw error;
  }
};

/**
 * Fires the CDP `FOVStatus` event carrying the current FOV appointment status.
 * Non-blocking: any failure is swallowed so it never interrupts the booking flow.
 */
export const trackFovAppointmentStatusEvent = (payload: BookAppointmentPayload): void => {
  try {
    cdpService.event(CDP_EVENTS.FOV_STATUS, {
      AppointmentStatus: payload.appointmentStatus,
    });
  } catch (error) {
    logger.error(`${LOG_PREFIX}: trackFovAppointmentStatusEvent failed`, {
      appointmentStatus: payload.appointmentStatus,
      error,
    });
  }
};

export const updateCDPAppointmentStatus = async (
  payload: BookAppointmentPayload
): Promise<void> => {
  const guestRef = getCookie('bx_guest_ref') as string;
  const guestType = 'customer';
  const url = `/api/contacts/UpdateFOVAppointmentDetails/${guestRef}?guestType=${guestType}`;
  const appointmentDateTime =
    payload.appointmentDateTimeDisplay ??
    buildAppointmentDateTimeDisplay(
      payload.appointmentDateLocal,
      payload.appointmentTime,
      payload.appointmentTimezone
    );
  try {
    await cdpPost(url, {
      enrollmentId: getOrCreateEnrollmentId(),
      appointmentDateTime,
      appointmentStatus: payload.appointmentStatus,
      guestRef,
    });
    // Emit the FOV status event once the CDP push has succeeded.
    trackFovAppointmentStatusEvent(payload);
    logger.info(`${LOG_PREFIX}: updateCDPAppointmentStatus success`, {
      ePrId: payload.ePrId,
      appointmentStatus: payload.appointmentStatus,
      guestRef,
    });
  } catch (error) {
    logger.error(`${LOG_PREFIX}: updateCDPAppointmentStatus failed`, {
      ePrId: payload.ePrId,
      appointmentStatus: payload.appointmentStatus,
      guestRef,
      error,
    });
    throw error;
  }
};
